import * as React from 'react';
import { useState } from 'react';

import { toast } from 'react-toastify';

import { Modal, ModalHeader } from '@emburse/ui-core';

import { BulkSkipPanel } from '@ReactComponents/BulkSkipPanel';
import { ManageCardsPanel } from '@ReactComponents/ManageCardsPanel';
import { ErrorAlert, parseAndGetErrorMessage } from '@ReactComponents/Shared/ErrorAlert';

import { LinkedAccountsApiService } from '@ClientApi/linkedAccounts';
import { ICardExtension } from '@ClientModels/card_extension';
import { IUserExtension } from '@ClientModels/userExtension';
import { ILinkedAccountModel } from '@Shared/models';

import * as style from './ManageCardProgramModal.styl';

interface ManageCardProgramInterface {
  closeModal: () => void;
  cards: ILinkedAccountModel[];
  users: IUserExtension[];
  cardsUpdated?: () => void;
  transactionsBulkSkipped?: () => void;
}

const getUserByCardId = (cards: ICardExtension[], users: IUserExtension[]) => {
  return cards.reduce<{ [key: string]: IUserExtension }>((accumulator, card) => {
    accumulator[card.map_id] = users.find((user) => user.id === card.assigned_user_id) || null;

    return accumulator;
  }, {});
};

export const ManageCardProgramModal: React.FC<ManageCardProgramInterface> = ({
  closeModal,
  cards,
  users,
  cardsUpdated = () => {},
  transactionsBulkSkipped = () => {}
}) => {
  const extendedCards: ICardExtension[] = cards.map((card) => ({ ...card, map_id: card.id }));
  const activeCards = extendedCards.filter((card) => card.state !== 'deleted');
  const usersByCardIdInitialState: { [key: string]: IUserExtension } = getUserByCardId(extendedCards, users);
  const [showBulk, setShowBulk] = useState(false);
  const [editedCards, setEditedCards] = useState<ICardExtension[]>([]);
  const [usersByCardId, setUsersByCardId] = useState(usersByCardIdInitialState);
  const [errorMessage, setErrorMessage] = useState('');
  const [cardList, setCardList] = useState(extendedCards);
  const [loading, setLoading] = useState(false);

  const removeUser = (card: ICardExtension) => {
    setUsersByCardId({ ...usersByCardId, [card.map_id]: null });
    setEditedCards([...editedCards, card]);
  };

  const updateCardUser = (user: IUserExtension, card: ICardExtension) => {
    const cardId = card.id;
    const changedCardIds = editedCards.map((editedCard) => editedCard.id);
    if (!changedCardIds.includes(cardId)) {
      setEditedCards([...editedCards, card]);
    }

    setUsersByCardId({ ...usersByCardId, [cardId]: user });
  };

  const cancelCard = async (card: ILinkedAccountModel) => {
    if (card.provider !== 'emburse_cards_gateway' || card.institution_type !== 'emburse') {
      setErrorMessage('Cannot cancel a card whose institution type is not Emburse.');
      return;
    }

    try {
      await LinkedAccountsApiService.cancel(card.id);

      toast.success('Card canceled');
      cardsUpdated();
      setCardList(
        cardList.map((cardFromList) => {
          if (cardFromList.id === card.id) {
            return { ...cardFromList, state: 'canceled' };
          }
          return cardFromList;
        })
      );
    } catch (error) {
      const newErrorMessage = parseAndGetErrorMessage(error.message);
      setErrorMessage(newErrorMessage);
    }
  };

  const removeCard = async (card: ILinkedAccountModel) => {
    if (!card.is_user_connection && !card.parent_linked_account_id) {
      return;
    }

    try {
      await LinkedAccountsApiService.deleteCard(card.id);

      toast.success('Card unassigned and deleted');
      cardsUpdated();
      closeModal();
    } catch (error) {
      const newErrorMessage = parseAndGetErrorMessage(error.message);
      setErrorMessage(newErrorMessage);
    }
  };

  const save = async () => {
    let newCards = editedCards.map((card): ICardExtension => {
      if (usersByCardId[card.map_id]) {
        card.assigned_user_id = usersByCardId[card.map_id].id;
      } else {
        card.assigned_user_id = null;
      }

      return card;
    });

    setLoading(true);
    try {
      await LinkedAccountsApiService.updateCompanyCardPrograms(newCards);
      toast.success('Card mapping updated');
      setLoading(false);
      setEditedCards([]);
      cardsUpdated();
      closeModal();
    } catch (error) {
      const newErrorMessage = parseAndGetErrorMessage(error.message);
      setErrorMessage(newErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  const onBulkSkipConfirm = async (beforeDate: Date, accountIds: string[]) => {
    setLoading(true);

    try {
      await LinkedAccountsApiService.skipCardTransactions(beforeDate, accountIds);

      toast.success(`Transactions skipped for ${accountIds.length} card(s).`);
      transactionsBulkSkipped();
      closeModal();
    } catch (error) {
      const newErrorMessage = parseAndGetErrorMessage(error.message);
      setErrorMessage(newErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={true} isLoading={loading}>
      <div className={style.global}>
        <ModalHeader title="Manage Card Program" showClose={true} onCloseClicked={closeModal} />
        {errorMessage && (
          <div className="error-wrapper">
            <ErrorAlert errorMessage={errorMessage} />
          </div>
        )}
        {!showBulk && (
          <ManageCardsPanel
            cards={cardList}
            users={users}
            usersByCardId={usersByCardId}
            cancelCard={cancelCard}
            deleteCard={removeCard}
            closeModal={closeModal}
            removeUser={removeUser}
            save={save}
            showBulkSkipView={() => setShowBulk(true)}
            updateCardUser={updateCardUser}
          />
        )}
        {showBulk && (
          <BulkSkipPanel
            cards={activeCards}
            usersByCardId={usersByCardId}
            onBulkSkipConfirmed={onBulkSkipConfirm}
            onCancel={closeModal}
            onError={(error) => setErrorMessage(error)}
          />
        )}
      </div>
    </Modal>
  );
};

// eslint-disable-next-line no-restricted-syntax
export default ManageCardProgramModal;
